import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { Frame, DragState, ResizeState } from '../types/frame'
import { useUiStore } from './uiStore'

const COLORS: Frame['color'][] = ['blue', 'green', 'orange', 'purple', 'red', 'cyan']
const DESKTOP_WIDTH = 4000
const DESKTOP_HEIGHT = 3000
const MIN_FRAME_SPACING = 5
const MIN_WIDTH = 150
const MIN_HEIGHT = 100
const SPACING = 40
const COLLISION_ITERATIONS = 10
const SESSION_FRAMES_KEY = 'bible_study_frames'

export const useFrameStore = defineStore('frames', () => {
  const frames = ref<Frame[]>([])
  const nextFrameId = ref(0)
  const dragState = ref<DragState | null>(null)
  const resizeState = ref<ResizeState | null>(null)
  const infoLabelVisible = ref(false)
  const infoLabelText = ref('')
  const infoLabelPosition = ref({ x: 0, y: 0 })

  const isInfoLabelVisible = computed(() => infoLabelVisible.value)

  // 保存 frames 到 sessionStorage
  const saveFramesToSession = (framesToSave: Frame[]) => {
    try {
      sessionStorage.setItem(SESSION_FRAMES_KEY, JSON.stringify(framesToSave))
    } catch (error) {
      console.error('Failed to save frames to sessionStorage:', error)
    }
  }

  // 監聽 frames 變化，自動保存到 sessionStorage
  watch(
    () => frames.value,
    (newFrames) => {
      if (newFrames.length > 0) {
        saveFramesToSession(newFrames)
      }
    },
    { deep: true }
  )

  const initializeFrames = async () => {
    const uiStore = useUiStore()
    const width = window.innerWidth / 3
    const height = (window.innerHeight - uiStore.toolbarHeight) / 2

    frames.value = [
      {
        id: 0,
        frameId: 0,
        x: 60,
        y: 60,
        w: width,
        h: height,
        title: '功能框 1',
        type: 'bible-reading',
        color: COLORS[0],
      },
    ]
    nextFrameId.value = 1
  }

  const addFrame = (customFrame?: Partial<Frame>) => {
    const uiStore = useUiStore()
    const width = window.innerWidth / 3
    const height = (window.innerHeight - uiStore.toolbarHeight) / 2

    // 如果提供了自訂框架，使用它
    if (customFrame) {
      const newFrame: Frame = {
        id: nextFrameId.value,
        frameId: customFrame.frameId || nextFrameId.value,
        x: customFrame.x ?? 60,
        y: customFrame.y ?? 60,
        w: customFrame.w ?? width,
        h: customFrame.h ?? height,
        title: customFrame.title ?? `功能框 ${nextFrameId.value + 1}`,
        type: customFrame.type ?? 'bible-reading',
        color: customFrame.color ?? COLORS[nextFrameId.value % COLORS.length],
        _noteInfo: customFrame._noteInfo,
        _isReadingMode: customFrame._isReadingMode,
        _mapInfo: customFrame._mapInfo,
        _bibleInfo: customFrame._bibleInfo,
      }
      frames.value.push(newFrame)
      nextFrameId.value++
      return
    }

    // 預設行為：創建新的聖經閱讀框
    if (frames.value.length === 0) {
      frames.value.push({
        id: nextFrameId.value,
        frameId: nextFrameId.value,
        x: 60,
        y: 60,
        w: width,
        h: height,
        title: `功能框 ${nextFrameId.value + 1}`,
        type: 'bible-reading',
        color: COLORS[nextFrameId.value % COLORS.length],
      })
      nextFrameId.value++
      return
    }

    const topFrames = frames.value.filter(f => Math.abs(f.y - 60) <= 20)
    let newX = 60
    if (topFrames.length > 0) {
      const rightmost = topFrames.reduce((max, f) => f.x + f.w > max.x + max.w ? f : max)
      newX = rightmost.x + rightmost.w + SPACING
    }

    let newY = 60
    while (frames.value.some(f => !(newX + width <= f.x || newX >= f.x + f.w || newY + height <= f.y || newY >= f.y + f.h))) {
      newX += width + SPACING
    }

    frames.value.push({
      id: nextFrameId.value,
      frameId: nextFrameId.value,
      x: Math.min(newX, DESKTOP_WIDTH - width),
      y: newY,
      w: width,
      h: height,
      title: `功能框 ${nextFrameId.value + 1}`,
      type: 'bible-reading',
      color: COLORS[nextFrameId.value % COLORS.length],
    })
    nextFrameId.value++
  }

  const removeFrame = (frameId: number) => {
    frames.value = frames.value.filter(f => f.id !== frameId)
  }

  const updateFrameType = async (frameId: number, newType: Frame['type']) => {
    const frame = frames.value.find(f => f.id === frameId)
    if (!frame) return

    frame.type = newType

    try {
      const { apiClient } = await import('../services/apiClient')
      await apiClient.saveFramesState(frames.value)
    } catch (error) {
      console.error('Failed to save frame type change:', error)
    }
  }

  const checkOverlap = (a: Frame, b: Frame): boolean => {
    return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y)
  }

  const getCollisionSide = (movedFrame: Frame, other: Frame): 'left' | 'right' | 'top' | 'bottom' => {
    const leftDist = movedFrame.x + movedFrame.w - other.x
    const rightDist = other.x + other.w - movedFrame.x
    const topDist = movedFrame.y + movedFrame.h - other.y
    const bottomDist = other.y + other.h - movedFrame.y

    const minDist = Math.min(leftDist, rightDist, topDist, bottomDist)

    if (minDist === leftDist && leftDist > 0) return 'left'
    if (minDist === rightDist && rightDist > 0) return 'right'
    if (minDist === topDist && topDist > 0) return 'top'
    return 'bottom'
  }

  const rollbackFrames = (movedStates: Map<number, { x: number; y: number }>) => {
    movedStates.forEach((state, frameId) => {
      const frame = frames.value.find(f => f.id === frameId)
      if (frame) {
        frame.x = state.x
        frame.y = state.y
      }
    })
  }

  const resolveCollisions = (movedId: number, proposedX: number, proposedY: number) => {
    const movedFrame = frames.value.find(f => f.id === movedId)
    if (!movedFrame || !dragState.value) return

    const movedStates = new Map<number, { x: number; y: number }>()
    movedStates.set(movedId, { x: dragState.value.lastX, y: dragState.value.lastY })

    movedFrame.x = proposedX
    movedFrame.y = proposedY

    const moved = new Set([movedId])

    for (let iteration = 0; iteration < COLLISION_ITERATIONS; iteration++) {
      let foundCollision = false

      for (const other of frames.value) {
        if (moved.has(other.id)) continue

        let hasCollision = checkOverlap(movedFrame, other)

        if (!hasCollision) {
          for (const movedFrameId of moved) {
            if (movedFrameId === movedId) continue
            const movedFrame2 = frames.value.find(f => f.id === movedFrameId)
            if (movedFrame2 && checkOverlap(movedFrame2, other)) {
              hasCollision = true
              break
            }
          }
        }

        if (!hasCollision) continue

        foundCollision = true

        let pushedByFrame = movedFrame
        if (!checkOverlap(movedFrame, other)) {
          for (const movedFrameId of moved) {
            if (movedFrameId === movedId) continue
            const movedFrame2 = frames.value.find(f => f.id === movedFrameId)
            if (movedFrame2 && checkOverlap(movedFrame2, other)) {
              pushedByFrame = movedFrame2
              break
            }
          }
        }

        const side = getCollisionSide(pushedByFrame, other)

        let targetX = other.x
        let targetY = other.y

        if (side === 'left') {
          targetX = pushedByFrame.x + pushedByFrame.w + MIN_FRAME_SPACING
        } else if (side === 'right') {
          targetX = pushedByFrame.x - other.w - MIN_FRAME_SPACING
        } else if (side === 'top') {
          targetY = pushedByFrame.y + pushedByFrame.h + MIN_FRAME_SPACING
        } else if (side === 'bottom') {
          targetY = pushedByFrame.y - other.h - MIN_FRAME_SPACING
        }

        const clampedX = Math.max(0, Math.min(targetX, DESKTOP_WIDTH - other.w))
        const clampedY = Math.max(MIN_FRAME_SPACING, Math.min(targetY, DESKTOP_HEIGHT - other.h))

        if (clampedX !== targetX || clampedY !== targetY) {
          rollbackFrames(movedStates)
          return
        }

        const tempOther = { ...other, x: clampedX, y: clampedY, w: other.w, h: other.h }
        if (checkOverlap(tempOther, movedFrame)) {
          rollbackFrames(movedStates)
          handleDragEnd()
          return
        }

        if (!movedStates.has(other.id)) {
          movedStates.set(other.id, { x: other.x, y: other.y })
        }

        other.x = clampedX
        other.y = clampedY
        moved.add(other.id)
      }

      if (!foundCollision) break
    }

    for (const other of frames.value) {
      if (other.id === movedId) continue
      if (checkOverlap(movedFrame, other)) {
        rollbackFrames(movedStates)
        handleDragEnd()
        return
      }
    }
  }

  const startDrag = (frameId: number, event: MouseEvent) => {
    const frame = frames.value.find(f => f.id === frameId)
    if (!frame) return

    const viewport = document.querySelector('[class*="overflow-auto"]') as HTMLElement
    const offsetX = event.clientX + (viewport?.scrollLeft || 0) - frame.x
    const offsetY = event.clientY + (viewport?.scrollTop || 0) - frame.y

    dragState.value = {
      frameId,
      offsetX,
      offsetY,
      lastX: frame.x,
      lastY: frame.y,
      mainAxis: null,
    }

    document.addEventListener('mousemove', handleDragMove)
    document.addEventListener('mouseup', handleDragEnd)
  }

  const handleDragMove = (event: MouseEvent) => {
    if (!dragState.value || !(event.buttons & 1)) {
      handleDragEnd()
      return
    }

    const frame = frames.value.find(f => f.id === dragState.value!.frameId)
    if (!frame) return

    const viewport = document.querySelector('[class*="overflow-auto"]') as HTMLElement
    const scrollLeft = viewport?.scrollLeft || 0
    const scrollTop = viewport?.scrollTop || 0

    let newX = event.clientX + scrollLeft - dragState.value.offsetX
    let newY = event.clientY + scrollTop - dragState.value.offsetY

    newX = Math.max(0, Math.min(newX, DESKTOP_WIDTH - frame.w))
    newY = Math.max(MIN_FRAME_SPACING, Math.min(newY, DESKTOP_HEIGHT - frame.h))

    const dx = newX - dragState.value.lastX
    const dy = newY - dragState.value.lastY

    if (dragState.value.mainAxis === null && (Math.abs(dx) > 0 || Math.abs(dy) > 0)) {
      dragState.value.mainAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
    }

    frame.x = newX
    frame.y = newY

    resolveCollisions(dragState.value.frameId, newX, newY)
    updateInfoLabel(frame.x, frame.y, frame.w, frame.h, event)

    dragState.value.lastX = frame.x
    dragState.value.lastY = frame.y
  }

  const handleDragEnd = async () => {
    hideInfoLabel()
    dragState.value = null
    document.removeEventListener('mousemove', handleDragMove)
    document.removeEventListener('mouseup', handleDragEnd)

    if (frames.value.length > 0) {
      try {
        const { apiClient } = await import('../services/apiClient')
        await apiClient.saveFramesState(frames.value)
      } catch (error) {
        console.error('Failed to save frames state:', error)
      }
    }
  }

  const startResize = (frameId: number, event: MouseEvent) => {
    const frame = frames.value.find(f => f.id === frameId)
    if (!frame) return

    resizeState.value = {
      frameId,
      startX: event.clientX,
      startY: event.clientY,
      startW: frame.w,
      startH: frame.h,
    }

    document.addEventListener('mousemove', handleResizeMove)
    document.addEventListener('mouseup', handleResizeEnd)
  }

  const handleResizeMove = (event: MouseEvent) => {
    if (!resizeState.value) return

    const frame = frames.value.find(f => f.id === resizeState.value!.frameId)
    if (!frame) return

    const deltaX = event.clientX - resizeState.value.startX
    const deltaY = event.clientY - resizeState.value.startY

    frame.w = Math.max(MIN_WIDTH, resizeState.value.startW + deltaX)
    frame.h = Math.max(MIN_HEIGHT, resizeState.value.startH + deltaY)

    updateInfoLabel(frame.x, frame.y, frame.w, frame.h, event)
  }

  const handleResizeEnd = async () => {
    hideInfoLabel()
    resizeState.value = null
    document.removeEventListener('mousemove', handleResizeMove)
    document.removeEventListener('mouseup', handleResizeEnd)

    if (frames.value.length > 0) {
      try {
        const { apiClient } = await import('../services/apiClient')
        await apiClient.saveFramesState(frames.value)
      } catch (error) {
        console.error('Failed to save frames state:', error)
      }
    }
  }

  const updateInfoLabel = (x: number, y: number, w: number, h: number, event: MouseEvent) => {
    infoLabelVisible.value = true
    infoLabelText.value = `X: ${Math.round(x)} Y: ${Math.round(y)} | W: ${Math.round(w)} H: ${Math.round(h)}`
    infoLabelPosition.value = {
      x: event.clientX + 12,
      y: event.clientY + 12,
    }
  }

  const hideInfoLabel = () => {
    infoLabelVisible.value = false
  }

  // 從 sessionStorage 恢復 frames
  const restoreFramesFromSession = () => {
    try {
      const savedFrames = sessionStorage.getItem(SESSION_FRAMES_KEY)
      if (savedFrames) {
        const parsedFrames = JSON.parse(savedFrames) as Frame[]
        frames.value = parsedFrames
        // 更新 nextFrameId 以避免 ID 衝突
        if (parsedFrames.length > 0) {
          const maxId = Math.max(...parsedFrames.map(f => f.id))
          nextFrameId.value = maxId + 1
        }
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to restore frames from sessionStorage:', error)
      return false
    }
  }

  // 清除 sessionStorage 中的 frames
  const clearFramesFromSession = () => {
    try {
      sessionStorage.removeItem(SESSION_FRAMES_KEY)
    } catch (error) {
      console.error('Failed to clear frames from sessionStorage:', error)
    }
  }

  return {
    frames,
    isInfoLabelVisible,
    infoLabelText,
    infoLabelPosition,
    dragState,
    resizeState,
    initializeFrames,
    addFrame,
    removeFrame,
    updateFrameType,
    startDrag,
    handleDragMove,
    handleDragEnd,
    startResize,
    updateInfoLabel,
    hideInfoLabel,
    saveFramesToSession,
    restoreFramesFromSession,
    clearFramesFromSession,
  }
})
